import React, { useState, useEffect, useContext } from "react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { useNavigate } from "react-router-dom";
import { GoAlertFill } from "react-icons/go";
import { IoIosCheckmarkCircle } from "react-icons/io";
import Spinner from "../Assets/auth_spinner.svg";
import axios from "axios";
import { UserContext } from "../context/UserContext";
import "../styles/LoginPage.css";

function VerifyIdentityPage() {
  const {
    setName,
    setPrimaryEmail,
    setProfileUrl,
    setUserRole,
    setLastLogin,
    setIsEligible,
    setIsVerified,
    setVerificationEmail,
    verificationEmail,
  } = useContext(UserContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState(verificationEmail || "");
  const [otp, setOtp] = useState("");
  const [touched, setTouched] = useState({ email: false, otp: false });
  const [errors, setErrors] = useState({ email: "", otp: "" });
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);

  const [apiError, setApiError] = useState(null);
  const [apiSuccess, setApiSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Format email to show first 2 chars and first 2 chars after @
  const formatEmail = (email) => {
    if (email) {
      const [username, domain] = email.split("@");
      const formattedUsername =
        username.slice(0, 2) + "*".repeat(username.length - 2);
      const [domainName, tld] = domain.split(".");
      const formattedDomain =
        domainName.slice(0, 2) + "*".repeat(domainName.length - 2);
      return `${formattedUsername}@${formattedDomain}.${tld}`;
    }
  };

  useGSAP(() => {
    gsap.from(".floating-label", {
      duration: 0.8,
      y: 50,
      opacity: 0,
      stagger: 0.1,
      ease: "power2.out",
    });

    gsap.from("#submitBtn", {
      duration: 1,
      scale: 0.5,
      opacity: 0,
      delay: 0.3,
      ease: "power2.out",
    });
  }, []);

  useEffect(() => {
    let interval;
    if (isTimerActive) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsTimerActive(false);
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerActive]);

  const handleEmailChange = (e) => setEmail(e.target.value);

  const handleOtpChange = (e) => {
    const value = e.target.value;

    // Allow only 5 digits and restrict to numeric values
    if (/^\d{0,5}$/.test(value)) {
      setOtp(value);
      // Clear error when user starts typing
      if (errors.otp) {
        setErrors((prev) => ({ ...prev, otp: "" }));
      }
    }
  };

  const handleFocus = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    // Clear error when field gets focus
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const verifyOTP = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/verify-otp`,
        { email, otp }
      );

      if (response?.data?.success) {
        localStorage.setItem("token", response.data.data.token);
        const currentDate = new Date();
        const formattedDate = currentDate.toLocaleString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        });
        setLastLogin(formattedDate);
        setName(response.data.data.name || "Guest User");
        setPrimaryEmail(response.data.data.email);
        setProfileUrl(response.data.data.profileURL || null);
        setUserRole(response.data.data.userRole);
        setIsVerified(true);
        setVerificationEmail("");

        if (response.data.data.userRole === "Student") {
          setIsEligible(response.data.data.isEligible);
          navigate("/dashboard");
        }
      }
    } catch (error) {
      setApiError(
        error?.response?.data?.message ||
          "Unexpected error occurred, please try again"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    // Mark fields as touched when submitting
    setTouched({ email: true, otp: true });

    let hasError = false;
    const newErrors = { email: "", otp: "" };

    // Validate OTP
    if (!otp.trim()) {
      newErrors.otp = "OTP is required";
      hasError = true;
    } else if (otp.length !== 5) {
      newErrors.otp = "OTP must be 5 digits";
      hasError = true;
    }

    setErrors(newErrors);

    if (!hasError) {
      verifyOTP();
    }
  };

  const startTimer = () => {
    setIsTimerActive(true);
    setTimeLeft(30);
  };

  const resendOTP = async () => {
    try {
      setIsResending(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/resend-otp`,
        { email }
      );

      if (response?.data?.success) {
        setApiError(null);
        setApiSuccess(response.data.message);
        startTimer();
      }
    } catch (error) {
      setApiError(
        error?.response?.data?.message ||
          "Unexpected error occurred, please try again"
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleResend = () => {
    setOtp(""); // Clear OTP field
    setErrors((prev) => ({ ...prev, otp: "" })); // Clear any OTP errors
    setTouched((prev) => ({ ...prev, otp: false })); // Reset touched state
    resendOTP(); // Call resend function
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="bg-white p-10 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
          Verification Required
        </h2>
        <p className="text-sm text-gray-500 text-center mb-6">
          We've sent an OTP to your email. Please enter it below to verify your
          identity.
        </p>

        <div className="space-y-4">
          {/* Email Input */}
          <div className="floating-label">
            <input
              type="email"
              id="email"
              placeholder=" "
              readOnly
              className="bg-gray-200 text-gray-600"
              value={formatEmail(email)}
              onChange={handleEmailChange}
              onFocus={() => handleFocus("email")}
            />
            {/* <label htmlFor="email">Email</label> */}
          </div>

          {/* OTP Input */}
          <div className="floating-label relative">
            <input
              type="text"
              id="otp"
              placeholder=" "
              className={`focus:ring-1 focus:ring-[#457B9D] pr-10 ${
                touched.otp && errors.otp
                  ? "input-error border-red-500"
                  : "border-gray-300"
              }`}
              value={otp}
              onChange={handleOtpChange}
              onFocus={() => handleFocus("otp")}
              maxLength={5}
            />
            <label htmlFor="otp">Enter Code</label>
            <p
              className={`helper-text ${
                touched.otp && errors.otp ? "hidden" : ""
              }`}
            >
              Enter the 5-digit code sent to your email
            </p>
            {touched.otp && errors.otp && (
              <p className="error-message visible text-sm text-red-500 mt-1">
                {errors.otp}
              </p>
            )}
          </div>

          {apiError && (
            <div className="flex items-center gap-x-2 bg-red-100 border border-red-700 text-red-700 text-sm font-semibold px-4 py-4 rounded-md mb-4">
              <GoAlertFill className="size-5" />
              <p>{apiError}</p>
            </div>
          )}

          {apiSuccess && (
            <div className="flex items-center gap-x-2 bg-green-100 border border-green-700 text-green-700 text-sm font-semibold px-4 py-4 rounded-md mb-4">
              <IoIosCheckmarkCircle className="size-6" />
              <p>New OTP has been sent to your email.</p>
            </div>
          )}

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            id="submitBtn"
            className="w-full py-3 mt-4 bg-[#1E3557] text-white rounded-md hover:shadow-md"
          >
            {isLoading && (
              <img
                src={Spinner}
                alt=""
                className="size-6 absolute top-3 right-5"
              />
            )}
            Continue
          </button>
        </div>

        {/* Timer / Resend Link */}
        <div className="my-4 text-center">
          {isTimerActive ? (
            <p className="text-sm text-gray-500">
              Resend available in {timeLeft} seconds
            </p>
          ) : (
            <button
              onClick={handleResend}
              className="text-[#457B9D] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isResending}
            >
              Resend Code
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyIdentityPage;
